import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PaginatedDto, SchoolSummaryDto } from '@eskoolhub/types';
import { PageHeader } from '../components/AppShell';
import { useToast } from '../components/Toasts';
import { IconChevron, IconPlus, IconSearch } from '../components/Icons';
import { EmptyState, ErrorState, Modal, SchoolStatusChip, SchoolTile, SkeletonRows } from '../components/ui';
import { OnboardSchoolModal } from '../features/OnboardSchoolModal';
import { api, ApiRequestError } from '../lib/api';
import { formatDate, formatNumber } from '../lib/format';

type Tab = 'ALL' | 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'EXPIRING';

const TABS: { label: string; value: Tab }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
  { label: 'Suspended', value: 'SUSPENDED' },
  { label: 'Expiring', value: 'EXPIRING' },
];

type Sort = 'NEWEST' | 'OLDEST' | 'NAME_ASC' | 'NAME_DESC' | 'EXPIRY_SOONEST';

const SORTS: { label: string; value: Sort }[] = [
  { label: 'Newest first', value: 'NEWEST' },
  { label: 'Oldest first', value: 'OLDEST' },
  { label: 'Name A–Z', value: 'NAME_ASC' },
  { label: 'Name Z–A', value: 'NAME_DESC' },
  { label: 'Expiry soonest', value: 'EXPIRY_SOONEST' },
];

/** A trial ending inside a week, or a paid renewal inside a fortnight — the
 * same "about to lapse" idea, at the horizon that actually leaves time to act. */
function isExpiring(school: SchoolSummaryDto): boolean {
  if (school.status === 'SUSPENDED' || school.status === 'CHURNED') return false;
  if (school.onTrial) return school.trialDaysLeft !== null && school.trialDaysLeft <= 7;
  if (!school.renewsAt) return false;
  const daysLeft = (new Date(school.renewsAt).getTime() - Date.now()) / 86_400_000;
  return daysLeft >= 0 && daysLeft <= 14;
}

function matchesTab(school: SchoolSummaryDto, tab: Tab): boolean {
  switch (tab) {
    case 'ALL':
      return true;
    case 'ACTIVE':
      return school.status === 'ACTIVE';
    case 'INACTIVE':
      return school.status === 'CHURNED';
    case 'SUSPENDED':
      return school.status === 'SUSPENDED';
    case 'EXPIRING':
      return isExpiring(school);
  }
}

function sortSchools(schools: SchoolSummaryDto[], sort: Sort): SchoolSummaryDto[] {
  const rows = [...schools];
  switch (sort) {
    case 'NEWEST':
      return rows.sort((a, b) => (b.onboardedAt ?? '').localeCompare(a.onboardedAt ?? ''));
    case 'OLDEST':
      return rows.sort((a, b) => (a.onboardedAt ?? '').localeCompare(b.onboardedAt ?? ''));
    case 'NAME_ASC':
      return rows.sort((a, b) => a.name.localeCompare(b.name));
    case 'NAME_DESC':
      return rows.sort((a, b) => b.name.localeCompare(a.name));
    case 'EXPIRY_SOONEST':
      return rows.sort((a, b) => {
        const at = a.renewsAt ? new Date(a.renewsAt).getTime() : Infinity;
        const bt = b.renewsAt ? new Date(b.renewsAt).getTime() : Infinity;
        return at - bt;
      });
  }
}

export function SchoolsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<Tab>('ALL');
  const [sort, setSort] = useState<Sort>('NEWEST');
  const [onboarding, setOnboarding] = useState(false);
  const [managing, setManaging] = useState<SchoolSummaryDto | null>(null);

  // Status is filtered client-side (below) so every tab's count is real
  // against the same fetch, rather than firing a fresh request per tab.
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['schools', query],
    queryFn: () =>
      api.get<PaginatedDto<SchoolSummaryDto>>(
        `/api/schools?pageSize=100${query ? `&query=${encodeURIComponent(query)}` : ''}`,
      ),
  });

  const counts = useMemo(() => {
    const items = data?.items ?? [];
    return Object.fromEntries(TABS.map((t) => [t.value, items.filter((s) => matchesTab(s, t.value)).length])) as Record<
      Tab,
      number
    >;
  }, [data]);

  const rows = useMemo(() => {
    const items = (data?.items ?? []).filter((s) => matchesTab(s, tab));
    return sortSchools(items, sort);
  }, [data, tab, sort]);

  const reactivate = useMutation({
    mutationFn: (id: string) => api.post(`/api/schools/${id}/reactivate`),
    onSuccess: (_result, id) => {
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      void queryClient.invalidateQueries({ queryKey: ['school', id] });
      setManaging(null);
      toast.push('School reactivated', 'success');
    },
    onError: (err) => toast.push(err instanceof ApiRequestError ? err.message : 'Could not reactivate', 'error'),
  });

  return (
    <>
      <PageHeader
        title="Schools"
        meta={data ? `${data.total} total` : 'Loading…'}
        actions={
          <button className="btn" onClick={() => setOnboarding(true)}>
            <IconPlus fill="var(--ink)" />
            Onboard school
          </button>
        }
      />

      <div className="page">
        <div className="segmented" style={{ alignSelf: 'flex-start' }}>
          {TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              aria-pressed={tab === t.value}
              onClick={() => setTab(t.value)}
              style={{ padding: '10px 16px' }}
            >
              {t.label} <span className="caption">{counts[t.value] ?? 0}</span>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              background: 'var(--white)',
              border: '1px solid var(--rule)',
              borderRadius: 12,
              padding: '0 14px',
              minHeight: 44,
              flex: '1 1 320px',
              maxWidth: 420,
            }}
          >
            <IconSearch size={17} fill="var(--ink-soft)" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, code or city"
              style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, fontSize: 14 }}
            />
          </div>

          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as Sort)}
            style={{
              minHeight: 44,
              borderRadius: 12,
              border: '1px solid var(--rule)',
              background: 'var(--white)',
              padding: '0 12px',
              fontSize: 14,
            }}
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                Sort: {s.label}
              </option>
            ))}
          </select>
        </div>

        {error ? (
          <ErrorState
            message={error instanceof ApiRequestError ? error.message : 'Could not load schools'}
            onRetry={() => void refetch()}
          />
        ) : isPending ? (
          <SkeletonRows count={5} />
        ) : rows.length === 0 ? (
          <div className="card card--flush">
            <EmptyState
              title="No schools here yet"
              message={query || tab !== 'ALL' ? 'Nothing matches that filter.' : 'Onboard the first school to get going.'}
              action={
                <button className="btn btn--sm" onClick={() => setOnboarding(true)}>
                  Onboard school
                </button>
              }
            />
          </div>
        ) : (
          <div className="card card--flush">
            <table className="table">
              <thead>
                <tr>
                  <th>School details</th>
                  <th style={{ width: 130 }}>Plan</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Students</th>
                  <th style={{ width: 140 }}>Status</th>
                  <th style={{ width: 130 }}>Registered</th>
                  <th style={{ width: 130 }}>Subscription</th>
                  <th style={{ width: 130, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((school) => (
                  <tr key={school.id} onClick={() => navigate(`/schools/${school.id}`)}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        <SchoolTile initials={school.initials} primary={school.status === 'ACTIVE'} />
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span style={{ fontSize: 14.5, fontWeight: 700 }}>{school.name}</span>
                          <span className="caption">{school.city}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="tag">{school.planName ?? '—'}</span>
                      {school.hasCustomPricing ? (
                        <span className="tag tag--manual" style={{ marginLeft: 6 }}>
                          Negotiated
                        </span>
                      ) : null}
                    </td>
                    <td className="num">{formatNumber(school.studentCount)}</td>
                    <td>
                      <SchoolStatusChip status={school.status} trialDaysLeft={school.trialDaysLeft} />
                    </td>
                    <td className="caption">{formatDate(school.onboardedAt)}</td>
                    <td className="caption">{formatDate(school.renewsAt)}</td>
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn--soft btn--sm"
                        onClick={() => setManaging(school)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        Manage <IconChevron size={13} fill="var(--ink-soft)" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {onboarding ? <OnboardSchoolModal onClose={() => setOnboarding(false)} /> : null}

      {managing ? (
        <Modal title={`Manage ${managing.name}`} onClose={() => setManaging(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              className="module-row"
              style={{ cursor: 'pointer', textAlign: 'left' }}
              onClick={() => {
                setManaging(null);
                navigate(`/schools/${managing.id}`);
              }}
            >
              <div className="module-row__name">View full details</div>
              <div className="module-row__desc">Plan, pricing, branding, features and administrators.</div>
            </button>

            {managing.status === 'SUSPENDED' ? (
              <button
                className="module-row"
                style={{ cursor: 'pointer', textAlign: 'left', borderColor: 'var(--leaf)' }}
                onClick={() => reactivate.mutate(managing.id)}
                disabled={reactivate.isPending}
              >
                <div className="module-row__name">{reactivate.isPending ? 'Reactivating…' : 'Reactivate'}</div>
                <div className="module-row__desc">Restores access for staff and parents immediately.</div>
              </button>
            ) : (
              <button
                className="module-row"
                style={{ cursor: 'pointer', textAlign: 'left', borderColor: 'var(--coral)' }}
                onClick={() => {
                  setManaging(null);
                  navigate(`/schools/${managing.id}`);
                }}
              >
                <div className="module-row__name">Suspend</div>
                <div className="module-row__desc">Needs a reason — opens on the school's own page.</div>
              </button>
            )}
          </div>
        </Modal>
      ) : null}
    </>
  );
}
